/**
 * Garmin健康データ用Zustandストア
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { create } from 'zustand'

import {
  getGarminActivities,
  getGarminBodyBattery,
  getGarminDashboard,
  getGarminSleep,
  getGarminSteps15Min,
  getGarminWeekly,
} from '@/api/services/garmin'
import type {
  GarminActivitiesResponse,
  GarminActivitySummary,
  GarminBodyBatteryResponse,
  GarminDailySummary,
  GarminDashboardResponse,
  GarminSleepRaw,
  GarminSteps15Min,
} from '@/api/types/garmin'

type GarminState = {
  // ダッシュボードデータ（今日）
  dashboard: GarminDashboardResponse | null
  isDashboardLoading: boolean

  // 週間データ
  weeklyData: GarminDailySummary[]
  isWeeklyLoading: boolean

  // アクティビティ一覧
  activities: GarminActivitySummary[]
  activitiesTotal: number
  isActivitiesLoading: boolean

  // Body Battery
  bodyBattery: GarminBodyBatteryResponse | null
  isBodyBatteryLoading: boolean

  // 15分刻み歩数
  steps15Min: GarminSteps15Min[]
  isSteps15MinLoading: boolean

  // 睡眠データ
  sleep: GarminSleepRaw | null
  isSleepLoading: boolean

  // 選択中の日付
  selectedDate: string | null

  // エラー
  error: string | null

  // アクション
  fetchDashboard: (date?: string) => Promise<void>
  fetchWeekly: (endDate?: string) => Promise<void>
  fetchActivities: (limit?: number, start?: number) => Promise<void>
  fetchBodyBattery: (date?: string) => Promise<void>
  fetchSteps15Min: (date?: string) => Promise<void>
  fetchSleep: (date?: string) => Promise<void>
  setSelectedDate: (date: string | null) => void
  clearError: () => void
  reset: () => void
}

const initialState = {
  dashboard: null,
  isDashboardLoading: false,
  weeklyData: [],
  isWeeklyLoading: false,
  activities: [],
  activitiesTotal: 0,
  isActivitiesLoading: false,
  bodyBattery: null,
  isBodyBatteryLoading: false,
  steps15Min: [],
  isSteps15MinLoading: false,
  sleep: null,
  isSleepLoading: false,
  selectedDate: null,
  error: null,
}

export const useGarminStore = create<GarminState>((set, get) => ({
  ...initialState,

  // ダッシュボード取得
  fetchDashboard: async (date?: string) => {
    set({ isDashboardLoading: true, error: null })
    try {
      const dashboard = await getGarminDashboard(date)
      set({ dashboard })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'ダッシュボードの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isDashboardLoading: false })
    }
  },

  // 週間データ取得
  fetchWeekly: async (endDate?: string) => {
    set({ isWeeklyLoading: true, error: null })
    try {
      const response = await getGarminWeekly(endDate)
      set({ weeklyData: response.days })
    } catch (error) {
      const message = error instanceof Error ? error.message : '週間データの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isWeeklyLoading: false })
    }
  },

  // アクティビティ取得
  fetchActivities: async (limit = 20, start = 0) => {
    set({ isActivitiesLoading: true, error: null })
    try {
      const response: GarminActivitiesResponse = await getGarminActivities(start, limit)
      set({
        activities: start === 0 ? response.activities : [...get().activities, ...response.activities],
        activitiesTotal: response.total,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'アクティビティの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isActivitiesLoading: false })
    }
  },

  // Body Battery取得
  fetchBodyBattery: async (date?: string) => {
    set({ isBodyBatteryLoading: true, error: null })
    try {
      const bodyBattery = await getGarminBodyBattery(date)
      set({ bodyBattery })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Body Batteryの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isBodyBatteryLoading: false })
    }
  },

  // 15分刻み歩数取得
  fetchSteps15Min: async (date?: string) => {
    set({ isSteps15MinLoading: true, error: null })
    try {
      const steps15Min = await getGarminSteps15Min(date)
      set({ steps15Min })
    } catch (error) {
      const message = error instanceof Error ? error.message : '歩数データの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isSteps15MinLoading: false })
    }
  },

  // 睡眠データ取得
  fetchSleep: async (date?: string) => {
    set({ isSleepLoading: true, error: null })
    try {
      const sleep = await getGarminSleep(date)
      set({ sleep })
    } catch (error) {
      const message = error instanceof Error ? error.message : '睡眠データの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isSleepLoading: false })
    }
  },

  // 日付選択
  setSelectedDate: (date: string | null) => {
    set({ selectedDate: date })
  },

  // エラークリア
  clearError: () => {
    set({ error: null })
  },

  // リセット
  reset: () => {
    set(initialState)
  },
}))
